import type { RecordErrorFields, UsedBySubject } from "~/components/usedBy/usedByHelpers";
import type { BatchAction, BatchResult } from "~/hooks/useBatchLifecycle";

import formatInUseSentence from "~/components/usedBy/inUseSentence";
import findRefusedRecord from "~/components/usedBy/usedByHelpers";
import { BATCH_KEY_LIMIT, chunk } from "~/helpers/csvImport/resolveInBatches";
import formatLocaleNumber from "~/helpers/localeNumberFormat";
import i18n from "~/i18n/i18n";

export interface BulkPayloadItem {
	errors?: Array<{ message: string } & RecordErrorFields> | null;
}

export const parseBatchResult = (
	entityResults: BulkPayloadItem[] | null | undefined,
	topLevelErrors: Array<{ message: string }> | null | undefined,
	labels: string[],
	action: BatchAction
): BatchResult => {
	const totalCount = labels.length;
	if (!entityResults) {
		const topErrors = topLevelErrors?.map((e) => e.message) || ["Unknown error"];
		const refusedRecord = findRefusedRecord(topLevelErrors, labels[0] ?? "");
		return {
			action,
			failedCount: totalCount,
			failures: topErrors,
			refused: totalCount === 1 && refusedRecord ? [refusedRecord] : [],
			singleFailureMessage: totalCount === 1 ? topErrors[0] : undefined,
			totalCount
		};
	}
	const failures: string[] = [];
	const refused: UsedBySubject[] = [];
	let singleFailureMessage: string | undefined;
	entityResults.forEach((item, i) => {
		if (item.errors?.length) {
			const refusedRecord = findRefusedRecord(item.errors, labels[i] || "");
			const sentence = refusedRecord
				? formatInUseSentence(refusedRecord.kind, refusedRecord.name, refusedRecord.holders)
				: null;
			const message = sentence ?? item.errors[0].message;
			failures.push(`${labels[i] || "Unknown"}: ${message}`);
			if (refusedRecord) refused.push(refusedRecord);
			if (totalCount === 1) singleFailureMessage = message;
		}
	});
	return {
		action,
		failedCount: failures.length,
		failures,
		refused,
		singleFailureMessage,
		totalCount
	};
};

interface GetBatchRibbonControlsOptions {
	deleteDisabled?: boolean;
	deleteDisabledTooltip?: string;
	inactivateDisabled?: boolean;
	inactivateDisabledTooltip?: string;
	onActivate?: () => void;
	onDelete?: () => void;
	onInactivate?: () => void;
	selectedCount: number;
}

export const getBatchRibbonControls = ({
	deleteDisabled,
	deleteDisabledTooltip,
	inactivateDisabled,
	inactivateDisabledTooltip,
	onActivate,
	onDelete,
	onInactivate,
	selectedCount
}: GetBatchRibbonControlsOptions): GridRibbonControl[] => {
	if (selectedCount === 0) return [];

	return [
		{
			color: "inherit",
			height: 3,
			label: i18n.t("common:grid.selectedCount", {
				count: selectedCount,
				qty: formatLocaleNumber(selectedCount)
			}),
			onClick: () => {},
			position: "left",
			type: "info",
			variant: "text"
		},
		...(onActivate
			? [
					{
						color: "success" as const,
						height: 3 as const,
						icon: "check_circle",
						label: i18n.t("common:actions.activate"),
						onClick: onActivate,
						position: "left" as const,
						type: "button" as const,
						variant: "outlined" as const
					}
				]
			: []),
		...(onInactivate
			? [
					{
						color: "warning" as const,
						disabled: inactivateDisabled,
						disabledTooltip: inactivateDisabledTooltip,
						height: 3 as const,
						icon: "pause_circle",
						label: i18n.t("common:actions.inactivate"),
						onClick: onInactivate,
						position: "left" as const,
						type: "button" as const,
						variant: "outlined" as const
					}
				]
			: []),
		...(onDelete
			? [
					{
						color: "error" as const,
						disabled: deleteDisabled,
						disabledTooltip: deleteDisabledTooltip,
						height: 3 as const,
						icon: "delete",
						label: i18n.t("common:actions.delete"),
						onClick: onDelete,
						position: "left" as const,
						type: "button" as const,
						variant: "outlined" as const
					}
				]
			: [])
	];
};

export const mergeBatchResults = (results: BatchResult[], action: BatchAction): BatchResult => {
	if (results.length === 1) return results[0];
	const failures = results.flatMap((r) => r.failures);
	const refused = results.flatMap((r) => r.refused ?? []);
	const totalCount = results.reduce((sum, r) => sum + r.totalCount, 0);
	return {
		action,
		failedCount: results.reduce((sum, r) => sum + r.failedCount, 0),
		failures,
		refused,
		singleFailureMessage: totalCount === 1 ? results[0]?.singleFailureMessage : undefined,
		totalCount
	};
};

export const runBatchInChunks = async <TRow>(
	rows: readonly TRow[],
	action: BatchAction,
	runChunk: (chunkRows: TRow[]) => Promise<BatchResult>,
	onProgress?: (doneCount: number, totalCount: number) => void,
	size: number = BATCH_KEY_LIMIT
): Promise<BatchResult> => {
	const chunks = chunk(rows, size);
	if (chunks.length === 0) return { action, failedCount: 0, failures: [], refused: [], totalCount: 0 };
	const results: BatchResult[] = [];
	let doneCount = 0;
	for (const chunkRows of chunks) {
		results.push(await runChunk(chunkRows));
		doneCount += chunkRows.length;
		if (chunks.length > 1) onProgress?.(doneCount, rows.length);
	}
	return mergeBatchResults(results, action);
};
